import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack, TextField, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import TeslaMark from 'src/components/TeslaMark'
import { useBlockedReason } from 'src/components/useBlockedAction'

import { AppleTripError, appleTripStart } from 'src/api/appleTrip'
import { GoogleTripError, googleTripStart } from 'src/api/googleTrip'
import { NewAccountError, newAccountConfirm } from 'src/api/newAccount'
import { useConsoleSignInOptionsQuery } from 'src/api/queries/signIn/useConsoleSignInOptionsQuery/useConsoleSignInOptionsQuery'
import { teslaTripStart, teslaTripStartFailure } from 'src/api/teslaTrip'
import AppleSignInButton from 'src/components/AppleSignInButton'
import BlockedHint from 'src/components/BlockedHint'
import GoogleSignInButton from 'src/components/GoogleSignInButton'
import SignInOptions from 'src/components/SignInOptions'
import { invitationLinkTokenRead } from 'src/pages/InvitationPage/invitationLinkToken'
import { leaveFor } from 'src/utils/leaveFor'
import type { NewAccountQuestion } from './NewAccountStep'
import NewAccountStep, { newAccountQuestionText } from './NewAccountStep'
import SecondFactorStep from './SecondFactorStep'
import { useLogin } from './useLogin'

// ----------------------------------------------------------------------
// The sign-in form.
//
// A failed sign in is stated above the form, not thrown as a toast. A toast
// is dismissed by the next one and sits in a corner away from the fields
// that caused it, and on the one screen where a person has no account yet
// there is nothing else on the page for it to compete with. Inline it is,
// announced, and it clears when they try again.
// ----------------------------------------------------------------------

interface LoginFormProps {
  /** A failure handed over from a round trip that ended on another page. */
  initialError?: string
  /**
   * A Tesla sign-in that came back owing the account's authenticator code,
   * before its Tesla account is linked to it.
   */
  teslaSecondFactorTicket?: string
  /** The same, for a Google sign-in. */
  googleSecondFactorTicket?: string
  /** The same, for an Apple sign-in. */
  appleSecondFactorTicket?: string
  /**
   * A Tesla, Google or Apple sign-in that no console account uses: nothing
   * has been made, and the form asks whether to make one.
   */
  newAccount?: NewAccountQuestion
}

/** What the code step calls the account a provider sign-in came from. */
const NEW_ACCOUNT = {
  tesla: 'This Tesla account',
  google: 'This Google account',
  apple: 'This Apple ID',
} as const

/**
 * What the sign-in form says after "I already have one". A Tesla account is
 * linked by signing in with it, so after Tesla the way back is the Tesla
 * account the console account uses, and Continue with Tesla asks Tesla for it
 * rather than answering from its session with the one that came back. Two
 * addresses Gmail delivers to one inbox are two accounts to Tesla, which is
 * how a fleet's admin came back as a second Tesla account on 2026-10-31.
 */
const LINK_INSTEAD = {
  tesla:
    'Continue with Tesla again and sign in with the Tesla account your BeastOS account uses. Tesla counts addresses that differ by a dot or a + as separate accounts, even where Gmail delivers them to one inbox.',
  google:
    'Sign in the way you usually do, then link Google in Settings. From then on, Continue with Google opens your account.',
  apple:
    'Sign in the way you usually do, then link Apple in Settings. From then on, Continue with Apple opens your account.',
} as const

/**
 * Said after it when an invitation is waiting in this tab. None of the
 * form's ways in carries the invitation link: the invitation's own page does,
 * so that is where the linked account accepts it.
 */
const ACCEPT_AFTER = {
  tesla:
    'To accept your invitation, open it again from the email and continue with Tesla.',
  google:
    'To accept your invitation, open it again from the email and continue with Google.',
  apple:
    'To accept your invitation, open it again from the email and continue with Apple.',
} as const

const PROVIDER_NAME = {
  tesla: 'Tesla',
  google: 'Google',
  apple: 'Apple',
} as const

/**
 * The line under "Sign in": every way in the page offers, in the order the
 * buttons stand. Google and Apple are named only where they are set up, as
 * their buttons are. It said Tesla or an email address for months after both
 * were added, which told a team member signing in with Google that this was
 * not the page for them.
 */
const signInHint = ({
  google,
  apple,
}: {
  google: boolean
  apple: boolean
}): string => {
  const accounts = [google && 'Google', apple && 'Apple'].filter(Boolean)
  const providers =
    accounts.length > 0 ? `, your ${accounts.join(' or ')} account,` : ','
  return `Use the Tesla account that owns the fleet${providers} or the email address you were given access with.`
}

const LoginForm = ({
  initialError,
  teslaSecondFactorTicket,
  googleSecondFactorTicket,
  appleSecondFactorTicket,
  newAccount,
}: LoginFormProps) => {
  const {
    emailLogin,
    secondFactorLogin,
    secondFactorContinue,
    startOver,
    teslaSignInComplete,
    secondFactorTicket,
    secondFactorVia,
    loading,
    error,
  } = useLogin()

  // Asked once: answered either way, the question does not come back.
  const [question, setQuestion] = useState(newAccount)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string>()
  const [linkInstead, setLinkInstead] = useState<{
    via: NewAccountQuestion['via']
    invited: boolean
  }>()

  useEffect(() => {
    if (teslaSecondFactorTicket) {
      secondFactorContinue(teslaSecondFactorTicket, 'tesla')
    } else if (googleSecondFactorTicket) {
      secondFactorContinue(googleSecondFactorTicket, 'google')
    } else if (appleSecondFactorTicket) {
      secondFactorContinue(appleSecondFactorTicket, 'apple')
    }
  }, [
    teslaSecondFactorTicket,
    googleSecondFactorTicket,
    appleSecondFactorTicket,
    secondFactorContinue,
  ])

  // Each drawn only where that sign-in is set up: a button that can only
  // fail is worse than none.
  const offered = useConsoleSignInOptionsQuery().data?.consoleSignInOptions
  const googleOffered = offered?.google === true
  const appleOffered = offered?.apple === true
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [handover, setHandover] = useState(initialError)
  const [startError, setStartError] = useState<string>()

  // The handed-over failure belongs to the trip that just ended, so a new
  // attempt replaces it rather than stacking under it.
  useEffect(() => {
    if (loading) {
      setHandover(undefined)
    }
  }, [loading])

  const shown = error ?? startError ?? handover

  const onCreateAccount = useCallback(async () => {
    if (!question) {
      return
    }
    setCreating(true)
    setCreateError(undefined)
    try {
      const outcome = await newAccountConfirm(question.ticket)
      if ('secondFactorTicket' in outcome) {
        // An account took the address while the question was open: its own
        // code comes first, as for any sign-in that finds one.
        setQuestion(undefined)
        secondFactorContinue(outcome.secondFactorTicket, question.via)
        return
      }
      await teslaSignInComplete(
        outcome.token,
        outcome.invitation,
        outcome.invitation !== null,
      )
    } catch (failure) {
      setCreating(false)
      if (
        failure instanceof NewAccountError &&
        failure.code === 'SIGN_IN_EXPIRED'
      ) {
        setQuestion(undefined)
        setHandover(
          `That sign-in has expired. Continue with ${
            PROVIDER_NAME[question.via]
          } again.`,
        )
        return
      }
      setCreateError(
        failure instanceof NewAccountError
          ? failure.message
          : 'The account could not be made. Try again.',
      )
    }
  }, [question, secondFactorContinue, teslaSignInComplete])

  const onHaveAccount = useCallback(() => {
    if (question) {
      setLinkInstead({
        via: question.via,
        invited: invitationLinkTokenRead() !== null,
      })
    }
    setQuestion(undefined)
  }, [question])

  const signInBlocked = useBlockedReason(!email || !password)

  // After "I already have one" following a Tesla sign-in, Tesla's session
  // holds the account that came back, and would answer with it again.
  const switchTeslaAccount = linkInstead?.via === 'tesla'

  // The backend begins the trip and keeps it, bound to this browser; see
  // `teslaTrip`. A refusal (too many attempts, no connection) is said here.
  const onTeslaLogin = useCallback(async () => {
    setStartError(undefined)
    setHandover(undefined)
    try {
      leaveFor(
        await teslaTripStart(
          switchTeslaAccount
            ? { purpose: 'sign_in', switchAccount: true }
            : { purpose: 'sign_in' },
        ),
      )
    } catch (failure) {
      setStartError(teslaTripStartFailure(failure))
    }
  }, [switchTeslaAccount])

  const onGoogleLogin = useCallback(async () => {
    setStartError(undefined)
    setHandover(undefined)
    try {
      leaveFor(await googleTripStart({ purpose: 'sign_in' }))
    } catch (failure) {
      setStartError(
        failure instanceof GoogleTripError
          ? failure.message
          : 'Google sign in could not start. Check your connection and try again.',
      )
    }
  }, [])

  const onAppleLogin = useCallback(async () => {
    setStartError(undefined)
    setHandover(undefined)
    try {
      leaveFor(await appleTripStart({ purpose: 'sign_in' }))
    } catch (failure) {
      setStartError(
        failure instanceof AppleTripError
          ? failure.message
          : 'Apple sign in could not start. Check your connection and try again.',
      )
    }
  }, [])

  const onEmailLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (email && password) {
        await emailLogin(email, password)
      }
    },
    [email, password, emailLogin],
  )

  if (question && !secondFactorTicket) {
    return (
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h2">Create a BeastOS account?</Typography>
          <Typography variant="body2" color="text.secondary">
            {newAccountQuestionText(question)}
          </Typography>
        </Stack>

        {createError && (
          <Alert severity="error" role="alert">
            {createError}
          </Alert>
        )}

        <NewAccountStep
          loading={creating}
          onCreate={() => void onCreateAccount()}
          onDecline={onHaveAccount}
        />
      </Stack>
    )
  }

  if (secondFactorTicket) {
    return (
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h2">Enter the code</Typography>
          <Typography variant="body2" color="text.secondary">
            {secondFactorVia === 'password'
              ? 'Open your authenticator app and type the six-digit code it shows for Beast.'
              : `${NEW_ACCOUNT[secondFactorVia]} is new to your console account, so it asks once for the code your authenticator app shows for Beast.`}
          </Typography>
        </Stack>

        {shown && (
          <Alert severity="error" role="alert">
            {shown}
          </Alert>
        )}

        <SecondFactorStep
          loading={loading}
          onSubmit={(answer) => void secondFactorLogin(answer)}
          onStartOver={startOver}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h2">Sign in</Typography>
        <Typography variant="body2" color="text.secondary">
          {signInHint({ google: googleOffered, apple: appleOffered })}
        </Typography>
      </Stack>

      {shown && (
        <Alert severity="error" role="alert">
          {shown}
        </Alert>
      )}

      {linkInstead && !shown && (
        <Alert severity="info">
          {linkInstead.invited
            ? `${LINK_INSTEAD[linkInstead.via]} ${
                ACCEPT_AFTER[linkInstead.via]
              }`
            : LINK_INSTEAD[linkInstead.via]}
        </Alert>
      )}

      <SignInOptions>
        <Button
          /*
            `tesla`, not `error` with the background painted over. Asking for
            one semantic and then replacing its fill takes the fill from one
            decision and the ink from another, which is how this button ended
            up drawing near black on dark red.
          */
          variant="contained"
          onClick={onTeslaLogin}
          color="tesla"
          disabled={loading}
          size="large"
          fullWidth
          // The real mark, not a letter set in the display face. The thing
          // being named here is Tesla, and `inherit` because the button
          // already states its own ink against the brand red; the default
          // would draw the mark in the page's primary ink on a red fill.
          startIcon={<TeslaMark inherit />}
        >
          Continue with Tesla
        </Button>

        {googleOffered && (
          <GoogleSignInButton
            size="large"
            fullWidth
            disabled={loading}
            onClick={() => void onGoogleLogin()}
          />
        )}

        {appleOffered && (
          <AppleSignInButton
            size="large"
            fullWidth
            disabled={loading}
            onClick={() => void onAppleLogin()}
          />
        )}
      </SignInOptions>

      {/* Not a `Divider`. A divider carrying children is a panel label in
          this system, drawn without its rules, and this is a separator
          between two ways to sign in rather than the name of a section. */}
      <Typography variant="body2" color="text.secondary" textAlign="center">
        or
      </Typography>

      <Stack component="form" onSubmit={onEmailLogin} spacing={3}>
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          fullWidth
          required
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          fullWidth
          required
        />
        {/* The sentence is the form's, not the button's, because the step it
            names is two fields up. It is the one in the console that keeps
            its line drawn at every width: everywhere else the reason moved
            onto the pointer, and this is the single page where a person who
            cannot get in has nobody to ask, and a hint only a hover can reach is
            no help to them. It still gains the tooltip.

            Named as the form's foot, because that is what it is: the commit
            and the sentence about it. Unnamed, the button's own box was a
            full-width row holding one control and no words on a phone (R14),
            since the sentence is its sibling rather than its child. */}
        <Stack spacing={1} data-form-foot>
          <BlockedHint
            reason={
              signInBlocked.blocked ? 'Enter your email and password' : null
            }
            reasonProps={signInBlocked.reasonProps}
            // The sentence below is drawn whether or not it applies, so this
            // adds the tooltip and nothing else.
            sentenceElsewhere
            sx={{ width: '100%' }}
          >
            <LoadingButton
              type="submit"
              variant="outlined"
              size="large"
              loading={loading}
              // `aria-disabled`, not `disabled`: a control that leaves the tab
              // order announces its reason to nobody, which is the dead end
              // this replaces. The native `type="submit"` still refuses,
              // because an empty required field cannot submit the form.
              aria-disabled={signInBlocked.blocked}
              disabled={signInBlocked.blocked}
              fullWidth
            >
              Sign in with email
            </LoadingButton>
          </BlockedHint>
          <Typography
            {...signInBlocked.reasonProps}
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            Enter your email and password
          </Typography>
        </Stack>
        <Button
          component={RouterLink}
          to="/login/forgot"
          variant="text"
          size="small"
        >
          Forgot your password?
        </Button>
      </Stack>
    </Stack>
  )
}

export default LoginForm
